import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Save, X, CalendarDays, List as ListIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const getWeekday = (dateString) => {
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  return days[new Date(dateString).getDay()];
};

export default function DeliverySchedulesManagement() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ delivery_date: "", deadline: "", change_deadline: "" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ deadline: "", change_deadline: "" });
  const [monthFilter, setMonthFilter] = useState(""); // YYYY-MM

  useEffect(() => {
    fetchSchedules();
  }, []);

  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const fmtDate = (d) => format(d, "yyyy-MM-dd");
  const deliveryDates = useMemo(
    () =>
      schedules.map((s) => {
        const [y, m, d] = s.delivery_date.split("-").map(Number);
        return new Date(y, m - 1, d);
      }),
    [schedules]
  );

  const handleToggleCalendarDate = async (date) => {
    if (!date) return;
    const dateStr = fmtDate(date);
    try {
      const r = await axios.post(
        `${API}/delivery-schedules/toggle`,
        { delivery_date: dateStr },
        getAuthHeaders()
      );
      toast.success(
        r.data.action === "added"
          ? `${dateStr} を納品日として追加しました`
          : `${dateStr} を納品日から削除しました`
      );
      fetchSchedules();
    } catch (error) {
      toast.error("更新に失敗しました");
    }
  };

  const renderCalendarTab = () => (
    <div className="flex flex-wrap gap-6 items-start">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-2 inline-block">
        <Calendar
          mode="single"
          locale={ja}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          selected={undefined}
          onSelect={handleToggleCalendarDate}
          modifiers={{ delivery: deliveryDates }}
          classNames={{
            day: "h-8 w-8 p-0 font-normal rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 aria-selected:opacity-100",
            day_outside: "day-outside text-rose-300 bg-rose-50/50 hover:bg-rose-50",
            day_today: "ring-2 ring-slate-400",
          }}
          modifiersClassNames={{
            delivery:
              "!bg-emerald-500 !text-white font-semibold hover:!bg-emerald-600",
          }}
          className="rounded-sm"
        />
        <div className="px-3 py-2 text-xs text-slate-600 border-t border-slate-200 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />
            配送あり
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-rose-50 border border-rose-200" />
            <span className="text-rose-700 font-medium">休み</span>
          </span>
          <span className="text-slate-400">
            日付をクリックすると追加／削除が切替わります
          </span>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 max-w-sm">
        <p className="text-xs text-slate-600 leading-5">
          カレンダーから配送可能日を直接設定できます。<br />
          追加時は自動で発注締切（配送日前週月曜18:59）と変更締切（配送日の2日前18:59）が計算されます。
          細かな締切調整は「一覧／締切編集」タブから行ってください。
        </p>
        <div className="mt-3 text-xs text-slate-500">
          現在の登録件数:{" "}
          <span className="font-mono font-semibold text-slate-700">
            {schedules.length}
          </span>{" "}
          日
        </div>
      </div>
    </div>
  );

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/delivery-schedules`, getAuthHeaders());
      const sorted = [...response.data].sort((a, b) =>
        a.delivery_date.localeCompare(b.delivery_date)
      );
      setSchedules(sorted);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { delivery_date: formData.delivery_date };
      if (formData.deadline) payload.deadline = formData.deadline.replace("T", " ");
      if (formData.change_deadline) payload.change_deadline = formData.change_deadline.replace("T", " ");
      await axios.post(`${API}/delivery-schedules`, payload, getAuthHeaders());
      toast.success("納品スケジュールを追加しました");
      setDialogOpen(false);
      setFormData({ delivery_date: "", deadline: "", change_deadline: "" });
      fetchSchedules();
    } catch (error) {
      toast.error("スケジュールの追加に失敗しました");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このスケジュールを削除しますか？")) return;

    try {
      await axios.delete(`${API}/delivery-schedules/${id}`, getAuthHeaders());
      toast.success("スケジュールを削除しました");
      fetchSchedules();
    } catch (error) {
      toast.error("スケジュールの削除に失敗しました");
    }
  };

  const startEdit = (schedule) => {
    setEditingId(schedule.id);
    setEditData({
      deadline: (schedule.deadline || "").replace(" ", "T").slice(0, 16),
      change_deadline: (schedule.change_deadline || "").replace(" ", "T").slice(0, 16),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ deadline: "", change_deadline: "" });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(
        `${API}/delivery-schedules/${id}`,
        {
          deadline: editData.deadline ? editData.deadline.replace("T", " ") : null,
          change_deadline: editData.change_deadline ? editData.change_deadline.replace("T", " ") : null,
        },
        getAuthHeaders()
      );
      toast.success("期限を更新しました");
      cancelEdit();
      fetchSchedules();
    } catch (error) {
      toast.error("更新に失敗しました");
    }
  };

  const months = useMemo(() => {
    const s = new Set();
    schedules.forEach((sc) => s.add(sc.delivery_date.slice(0, 7)));
    return Array.from(s).sort();
  }, [schedules]);

  const filtered = monthFilter
    ? schedules.filter((s) => s.delivery_date.startsWith(monthFilter))
    : schedules;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800">
            納品日・期限管理
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            納品日、発注締切（デフォルト: 前週の月曜18:59）、変更締切（デフォルト: 2日前18:59）を管理します。編集可。
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="month-filter" className="text-sm text-slate-700">
              月:
            </Label>
            <select
              id="month-filter"
              data-testid="schedules-month-filter"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="h-9 px-3 rounded-sm border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全期間</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-schedule-button"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                スケジュールを追加
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>新しい納品スケジュール</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="delivery_date">納品日</Label>
                  <Input
                    id="delivery_date"
                    data-testid="schedule-date-input"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) =>
                      setFormData({ ...formData, delivery_date: e.target.value })
                    }
                    className="mt-1 h-9 rounded-sm"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="deadline">発注締切（空欄なら自動: 前週月曜18:59）</Label>
                  <Input
                    id="deadline"
                    data-testid="schedule-deadline-input"
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="mt-1 h-9 rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="change_deadline">変更締切（空欄なら自動: 2日前18:59）</Label>
                  <Input
                    id="change_deadline"
                    data-testid="schedule-change-deadline-input"
                    type="datetime-local"
                    value={formData.change_deadline}
                    onChange={(e) =>
                      setFormData({ ...formData, change_deadline: e.target.value })
                    }
                    className="mt-1 h-9 rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="schedule-submit-button"
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  追加
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger value="calendar" data-testid="tab-schedule-calendar" className="flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4" />
            カレンダー設定
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-schedule-list" className="flex items-center gap-1.5">
            <ListIcon className="h-4 w-4" />
            一覧／締切編集
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          {renderCalendarTab()}
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">読み込み中...</div>
          ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                  納品日
                </th>
                <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                  発注締切
                </th>
                <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                  変更締切
                </th>
                <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-40">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((schedule) => {
                const isEditing = editingId === schedule.id;
                return (
                  <tr
                    key={schedule.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50"
                  >
                    <td className="p-2 px-4 align-middle text-sm font-mono">
                      {schedule.delivery_date}{" "}
                      <span className="text-blue-600">
                        ({getWeekday(schedule.delivery_date)})
                      </span>
                    </td>
                    <td className="p-2 px-4 align-middle text-sm font-mono">
                      {isEditing ? (
                        <input
                          type="datetime-local"
                          value={editData.deadline}
                          onChange={(e) =>
                            setEditData({ ...editData, deadline: e.target.value })
                          }
                          data-testid={`edit-deadline-${schedule.id}`}
                          className="h-8 px-2 border border-slate-300 rounded-sm text-sm"
                        />
                      ) : (
                        (schedule.deadline || "").replace("T", " ")
                      )}
                    </td>
                    <td className="p-2 px-4 align-middle text-sm font-mono">
                      {isEditing ? (
                        <input
                          type="datetime-local"
                          value={editData.change_deadline}
                          onChange={(e) =>
                            setEditData({ ...editData, change_deadline: e.target.value })
                          }
                          data-testid={`edit-change-deadline-${schedule.id}`}
                          className="h-8 px-2 border border-slate-300 rounded-sm text-sm"
                        />
                      ) : (
                        (schedule.change_deadline || "").replace("T", " ")
                      )}
                    </td>
                    <td className="p-2 px-4 align-middle text-center">
                      {isEditing ? (
                        <div className="flex justify-center gap-1">
                          <Button
                            onClick={() => saveEdit(schedule.id)}
                            data-testid={`save-schedule-${schedule.id}`}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8"
                          >
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            onClick={cancelEdit}
                            variant="ghost"
                            size="sm"
                            className="h-8"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-center gap-1">
                          <Button
                            onClick={() => startEdit(schedule)}
                            data-testid={`edit-schedule-${schedule.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(schedule.id)}
                            data-testid={`delete-schedule-${schedule.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">スケジュールがありません</div>
          )}
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
